import type { Building } from "building-types/building/buildingTypes";
import type { LumberRailMemberInfo } from "building-types/framing/lumberRailMemberInfoTypes";
import type { LumberRail } from "building-types/framing/lumberTypes";
import { collectResolvedOpeningFramingFromWallFramingSystems } from "building-types/walls/resolvedOpeningFramingTools";
import type { ResolvedOpeningFraming } from "building-types/walls/wallFramingTypes";
import type { RWTable } from "common/data/table";
import {
  collectLumberRailsFromAllFramedFloorAssemblies,
  collectLumberRailsFromFloorSystems,
  collectLumberRailsFromWallFramingSystems,
} from "../generationTools/generateLumberRailsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  wallFramingSystems,
  gableEndWallFramingSystems,
  framedFloorAssemblies,
  floorSystems,
  doc,
}: Pick<
  Building,
  "wallFramingSystems" | "gableEndWallFramingSystems" | "framedFloorAssemblies" | "floorSystems" | "doc"
>) {
  return {
    wallFramingSystems,
    gableEndWallFramingSystems,
    framedFloorAssemblies,
    floorSystems,
    openings: doc.openings,
    floors: doc.floors,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  wallFramingSystems: "shallow",
  gableEndWallFramingSystems: "shallow",
  framedFloorAssemblies: "shallow",
  floorSystems: "shallow",
  openings: "shallow",
  floors: "shallow",
});

function generate({
  wallFramingSystems,
  gableEndWallFramingSystems,
  framedFloorAssemblies,
  floorSystems,
  openings,
  floors,
}: ReturnType<typeof select>): Pick<
  Building,
  "lumberRails" | "lumberRailToMemberInfo" | "openingToResolvedOpeningFraming"
> {
  const lumberRails: RWTable<LumberRail> = {};
  const lumberRailToMemberInfo: RWTable<LumberRailMemberInfo> = {};
  const openingToResolvedOpeningFraming: RWTable<ResolvedOpeningFraming> = {};

  collectLumberRailsFromWallFramingSystems(wallFramingSystems, openings, lumberRails, lumberRailToMemberInfo);
  collectLumberRailsFromWallFramingSystems(gableEndWallFramingSystems, openings, lumberRails, lumberRailToMemberInfo);
  collectResolvedOpeningFramingFromWallFramingSystems(wallFramingSystems, openingToResolvedOpeningFraming);
  collectResolvedOpeningFramingFromWallFramingSystems(gableEndWallFramingSystems, openingToResolvedOpeningFraming);
  collectLumberRailsFromAllFramedFloorAssemblies(framedFloorAssemblies, lumberRails, lumberRailToMemberInfo, floors);
  collectLumberRailsFromFloorSystems(floorSystems, lumberRails, lumberRailToMemberInfo);

  return {
    lumberRails,
    lumberRailToMemberInfo,
    openingToResolvedOpeningFraming,
  };
}

export default memo(select, generate, compare);
